import { useState, useEffect, useRef, useContext } from "react";
import { ThemeCtx } from "./AppShell";
import { C, API } from "./utils";

const PAGE       = 20;
const MEDIA_BASE = "https://cip.medsphere.net/kmpattach/";
const mediaUrl   = p => p ? MEDIA_BASE + p : "";
const EMOJI_LIST = ["👍","❤️","😂","😮","😢","👏","🔥","🎉"];

const parseReactions = r => {
  if (!r || typeof r !== "object") return {};
  const out = {};
  for (const [e,v] of Object.entries(r))
    out[e] = typeof v==="number"?{count:v,members:[]}:{count:v.count||0,members:v.members||[]};
  return out;
};

function Avatar({name,color,size=32}) {
  const ini=(name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,
    display:"flex",alignItems:"center",justifyContent:"center",
    fontSize:size*0.36,fontWeight:"800",color,flexShrink:0,userSelect:"none"}}>{ini}</div>;
}

// ─── Members popup ────────────────────────────────────────────────────────────
function MembersPopup({title,members,onClose}) {
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.3)",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:"14px",padding:"18px 20px",
        minWidth:"210px",maxWidth:"280px",boxShadow:"0 10px 40px rgba(0,0,0,0.18)"}}
        onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"12px"}}>
          <span style={{fontSize:"13px",fontWeight:"700",color:"#111"}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
            fontSize:"18px",color:"#9CA3AF",lineHeight:1,padding:0}}>×</button>
        </div>
        {!members.length
          ?<div style={{fontSize:"12px",color:"#9CA3AF",textAlign:"center",padding:"8px"}}>No members</div>
          :members.map((m,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 0",
              borderBottom:i<members.length-1?"1px solid #F3F4F6":"none"}}>
              <div style={{width:"26px",height:"26px",borderRadius:"50%",background:"#E5E7EB",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:"10px",fontWeight:"700",color:"#555",flexShrink:0}}>
                {(m.NAME||m.name||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:"12px",color:"#374151",fontWeight:"600"}}>{m.NAME||m.name}</div>
                {m.VIEWED&&<div style={{fontSize:"10px",color:"#9CA3AF"}}>Last seen: {m.VIEWED}</div>}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ─── Reaction pills ───────────────────────────────────────────────────────────
function ReactionPills({dmId,reactions:rawR,myReaction,user,color,onReacted,align="left"}) {
  const [popup,setPopup]=useState(null);
  const reactions=parseReactions(rawR);
  const pills=Object.entries(reactions).filter(([,v])=>v.count>0);
  const react=async emoji=>{
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("dm_id",dmId); fd.append("reaction",emoji);
      const r=await fetch(`${API}/react_dm`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      onReacted&&onReacted(res.DATA);
    }catch(e){}
  };
  if(!pills.length) return null;
  return(
    <>
      {popup&&<MembersPopup title={popup.title} members={popup.members} onClose={()=>setPopup(null)}/>}
      <div style={{display:"flex",alignItems:"center",gap:"3px",flexWrap:"wrap",
        justifyContent:align==="right"?"flex-end":"flex-start"}}>
        {pills.map(([emoji,v])=>(
          <button key={emoji} onClick={()=>setPopup({title:emoji,members:v.members})}
            title={v.members.map(m=>m.NAME).join(", ")}
            style={{padding:"1px 6px",borderRadius:"10px",cursor:"pointer",
              border:`1px solid ${myReaction===emoji?color:"#ddd"}`,
              background:myReaction===emoji?`${color}18`:"rgba(255,255,255,0.9)",
              display:"flex",alignItems:"center",gap:"2px",lineHeight:1.6,fontSize:"10px"}}>
            <span style={{fontSize:"12px"}}>{emoji}</span>
            <span style={{color:"#555",fontWeight:"600"}}>{v.count}</span>
          </button>
        ))}
      </div>
    </>
  );
}

// ─── Message Actions toolbar ──────────────────────────────────────────────────
function MessageActions({isMe,color,canDelete,onReply,onDelete,user,dmId,onReacted}) {
  const [emojiOpen,setEmojiOpen]=useState(false);
  const ref=useRef();
  useEffect(()=>{
    if(!emojiOpen) return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setEmojiOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[emojiOpen]);

  const react=async emoji=>{
    setEmojiOpen(false);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("dm_id",dmId); fd.append("reaction",emoji);
      const r=await fetch(`${API}/react_dm`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      onReacted&&onReacted(res.DATA);
    }catch(e){}
  };

  const Btn=({icon,label,fn,danger=false})=>{
    const [hov,setHov]=useState(false);
    return(
      <button onClick={fn} title={label}
        onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
        style={{background:hov?(danger?"#FEF2F2":`${color}12`):"none",
          border:"none",cursor:"pointer",borderRadius:"14px",
          padding:"4px 7px",display:"flex",alignItems:"center",
          gap:"3px",fontSize:"14px",transition:"background 0.12s",lineHeight:1}}>
        <span>{icon}</span>
        {hov&&<span style={{fontSize:"10px",fontWeight:"600",
          color:danger?"#EF4444":color,whiteSpace:"nowrap"}}>{label}</span>}
      </button>
    );
  };

  return(
    <div style={{display:"flex",alignItems:"center",gap:"1px",background:"#fff",
      borderRadius:"20px",boxShadow:"0 2px 10px rgba(0,0,0,0.14)",
      border:"1px solid #E5E7EB",padding:"3px 5px",position:"relative"}}>
      <Btn icon="↩️" label="Reply" fn={onReply}/>
      <div ref={ref} style={{position:"relative"}}>
        <Btn icon="😊" label="React" fn={()=>setEmojiOpen(s=>!s)}/>
        {emojiOpen&&(
          <div style={{position:"absolute",bottom:"calc(100% + 2px)",paddingBottom:"6px",
            ...(isMe?{right:0}:{left:0}),
            background:"#fff",borderRadius:"14px",
            boxShadow:"0 4px 20px rgba(0,0,0,0.16)",border:"1px solid #E5E7EB",
            padding:"6px 8px",display:"flex",gap:"3px",zIndex:600,whiteSpace:"nowrap"}}>
            {EMOJI_LIST.map(e=>(
              <button key={e} onClick={()=>react(e)}
                style={{background:"none",border:"none",cursor:"pointer",
                  fontSize:"18px",padding:"4px 5px",borderRadius:"8px",lineHeight:1,
                  transition:"transform 0.1s"}}
                onMouseEnter={ev=>ev.currentTarget.style.transform="scale(1.3)"}
                onMouseLeave={ev=>ev.currentTarget.style.transform="scale(1)"}>
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
      {canDelete&&<Btn icon="🗑" label="Delete" fn={onDelete} danger/>}
    </div>
  );
}

// ─── Quoted block ─────────────────────────────────────────────────────────────
function QuotedMsg({quoted,color,isMe}) {
  if(!quoted) return null;
  const name=quoted.NAME||`${quoted.FIRSTNAME||""} ${quoted.LASTNAME||""}`.trim()||"User";
  const preview=quoted.TYPE==="image"?"📷 Photo":quoted.TYPE==="video"?"🎥 Video":(quoted.MESSAGE||"");
  const scrollTo=()=>{
    const el=document.getElementById(`dm-${quoted.ID}`);
    if(!el) return;
    el.scrollIntoView({behavior:"smooth",block:"center"});
    el.style.transition="background 0.2s";
    el.style.background="rgba(0,0,0,0.08)";
    setTimeout(()=>{el.style.background="transparent";},1200);
  };
  return(
    <div onClick={scrollTo}
      style={{borderLeft:`3px solid ${isMe?"rgba(255,255,255,0.5)":color}`,
        background:isMe?"rgba(0,0,0,0.12)":"rgba(0,0,0,0.05)",
        padding:"4px 8px",borderRadius:"4px",marginBottom:"6px",cursor:"pointer"}}
      onMouseEnter={e=>e.currentTarget.style.background=isMe?"rgba(0,0,0,0.18)":"rgba(0,0,0,0.09)"}
      onMouseLeave={e=>e.currentTarget.style.background=isMe?"rgba(0,0,0,0.12)":"rgba(0,0,0,0.05)"}>
      <div style={{fontSize:"10px",fontWeight:"700",color:isMe?"rgba(255,255,255,0.85)":color,marginBottom:"1px"}}>{name}</div>
      <div style={{fontSize:"11px",color:isMe?"rgba(255,255,255,0.7)":"#6B7280",
        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"190px"}}>{preview}</div>
    </div>
  );
}

// ─── Shared document block ───────────────────────────────────────────────────
function SharedDoc({shared,color,isMe}) {
  if(!shared) return null;
  return(
    <div style={{background:isMe?"rgba(255,255,255,0.15)":"#F8FAFC",
      border:`1px solid ${isMe?"rgba(255,255,255,0.3)":"#E5E7EB"}`,
      borderRadius:"8px",padding:"8px 10px",marginBottom:"6px",
      display:"flex",gap:"8px",alignItems:"flex-start"}}>
      <span style={{fontSize:"20px",flexShrink:0}}>📄</span>
      <div style={{minWidth:0}}>
        <div style={{fontSize:"11px",fontWeight:"700",color:isMe?"rgba(255,255,255,0.9)":"#111",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shared.TITLE||"Shared Document"}</div>
        {shared.NOTES&&<div style={{fontSize:"10px",color:isMe?"rgba(255,255,255,0.6)":"#9CA3AF",marginTop:"1px",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shared.NOTES}</div>}
      </div>
    </div>
  );
}

// ─── Poll ─────────────────────────────────────────────────────────────────────
function PollItem({poll,msg,user,color,onAnswer}) {
  const [submitting,setSubmitting]=useState(false);
  const [selected,setSelected]=useState([]);
  const [textAns,setTextAns]=useState("");
  const [popup,setPopup]=useState(null);
  const expired=msg.EXPIRED==="YES";
  const hasAnswered=poll.MCQ!==0
    ?(poll.RESULT||[]).some(r=>(r.MEMBERS||[]).some(m=>String(m.USERID)===String(user.USERID)))
    :(poll.RESULT||[]).some(r=>String(r.USERID)===String(user.USERID));
  const totalVotes=(poll.RESULT||[]).reduce((s,r)=>s+(r.STATS||0),0);

  const submit=async()=>{
    if(submitting||(poll.MCQ===0&&!textAns.trim())||(poll.MCQ!==0&&!selected.length)) return;
    setSubmitting(true);
    try{
      const fd=new FormData();
      fd.append("sender",user.USERID);
      fd.append("groupid",msg.GROUPID||msg.DMID);
      fd.append("type","answer");
      fd.append("poll_answer",JSON.stringify(poll.MCQ===0
        ?{pollid:poll.POLLID,answer:textAns.trim(),mcq:0}
        :{pollid:poll.POLLID,answer:(poll.OPTIONS||[]).map((_,i)=>selected.includes(i)?"1":"0"),mcq:poll.MCQ}));
      const r=await fetch(`${API}/D_Message`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1"){setSelected([]);setTextAns("");onAnswer&&onAnswer();}
    }catch(e){}
    setSubmitting(false);
  };

  return(
    <div style={{minWidth:"200px"}}>
      {popup&&<MembersPopup title={popup.opt} members={popup.members} onClose={()=>setPopup(null)}/>}
      <div style={{fontSize:"11px",fontWeight:"700",marginBottom:"8px",
        display:"flex",alignItems:"center",gap:"5px",flexWrap:"wrap"}}>
        📊 {poll.POLL_QUESTION}
        {expired&&<span style={{fontSize:"9px",background:"rgba(239,68,68,0.15)",color:"#EF4444",
          padding:"1px 6px",borderRadius:"20px"}}>Expired</span>}
        <span style={{fontSize:"9px",opacity:0.6,marginLeft:"auto"}}>{msg.PRIVACY===1?"🔒":"🌐"}</span>
      </div>
      {(hasAnswered||expired)&&poll.MCQ!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
          {(poll.OPTIONS||[]).map((opt,i)=>{
            const res=poll.RESULT?.[i]||{};
            const isMe2=(res.MEMBERS||[]).some(m=>String(m.USERID)===String(user.USERID));
            const pct=parseFloat(res.PERCENTAGE)||0;
            return(
              <div key={i} onClick={()=>setPopup({opt,members:res.MEMBERS||[]})} style={{cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:"11px",
                  marginBottom:"3px",fontWeight:isMe2?"700":"400"}}>
                  <span>{isMe2&&"✓ "}{opt}</span>
                  <span style={{opacity:0.7}}>{res.STATS||0} · {res.PERCENTAGE||"0%"}</span>
                </div>
                <div style={{height:"5px",borderRadius:"3px",background:"rgba(255,255,255,0.25)",overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:"3px",
                    background:isMe2?"rgba(255,255,255,0.95)":"rgba(255,255,255,0.55)",
                    width:`${pct}%`,transition:"width 0.5s"}}/>
                </div>
              </div>
            );
          })}
          <div style={{fontSize:"9px",opacity:0.6,marginTop:"1px"}}>
            {totalVotes} response{totalVotes!==1?"s":""} · tap to see who voted
          </div>
        </div>
      )}
      {!hasAnswered&&!expired&&poll.MCQ!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"5px"}}>
          {(poll.OPTIONS||[]).map((opt,i)=>(
            <label key={i} style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",fontSize:"12px",userSelect:"none"}}>
              <input type={poll.MCQ===1?"radio":"checkbox"}
                checked={selected.includes(i)}
                onChange={()=>poll.MCQ===1?setSelected([i]):setSelected(s=>s.includes(i)?s.filter(x=>x!==i):[...s,i])}
                style={{accentColor:"#fff",cursor:"pointer",width:"14px",height:"14px"}}/>
              {opt}
            </label>
          ))}
          <button onClick={submit} disabled={submitting||!selected.length}
            style={{padding:"5px 14px",border:"none",borderRadius:"8px",
              background:"rgba(255,255,255,0.25)",color:"inherit",fontSize:"11px",
              fontWeight:"700",cursor:"pointer",alignSelf:"flex-start",
              opacity:!selected.length?"0.5":"1"}}>{submitting?"…":"Submit"}</button>
        </div>
      )}
    </div>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({src,onClose}) {
  useEffect(()=>{
    const h=e=>{if(e.key==="Escape")onClose();};
    document.addEventListener("keydown",h);
    return()=>document.removeEventListener("keydown",h);
  },[]);
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",
      zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
      <button onClick={onClose} style={{position:"absolute",top:"16px",right:"20px",
        background:"rgba(255,255,255,0.15)",border:"none",borderRadius:"50%",
        width:"36px",height:"36px",cursor:"pointer",color:"#fff",fontSize:"20px",
        display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
      <img src={src} alt="" onClick={e=>e.stopPropagation()}
        style={{maxWidth:"90vw",maxHeight:"88vh",borderRadius:"8px",objectFit:"contain"}}/>
    </div>
  );
}

// ─── Reply bar ────────────────────────────────────────────────────────────────
function ReplyBar({msg,color,onCancel}) {
  if(!msg) return null;
  const name=msg.NAME||`${msg.FIRSTNAME||""} ${msg.LASTNAME||""}`.trim()||"User";
  const preview=msg.TYPE==="image"?"📷 Photo":msg.TYPE==="video"?"🎥 Video":(msg.MESSAGE||"");
  return(
    <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"8px 12px",
      background:"#F8FAFC",borderTop:"1px solid #E5E7EB",borderLeft:`3px solid ${color}`}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:"10px",fontWeight:"700",color,marginBottom:"1px"}}>{name}</div>
        <div style={{fontSize:"11px",color:"#6B7280",overflow:"hidden",
          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{preview}</div>
      </div>
      <button onClick={onCancel} style={{background:"none",border:"none",cursor:"pointer",
        color:"#9CA3AF",fontSize:"18px",lineHeight:1,padding:0,flexShrink:0}}>×</button>
    </div>
  );
}

// ─── Single message bubble ────────────────────────────────────────────────────
function DMBubble({msg,prevMsg,user,color,isAdmin,onDelete,onReacted,onReply}) {
  const isMe    = String(msg.SENDER)===String(user.USERID);
  const name    = msg.NAME||`${msg.FIRSTNAME||""} ${msg.LASTNAME||""}`.trim()||"User";
  const isReply = !!msg.PARENT_ID;
  const prevSame= prevMsg&&String(prevMsg.SENDER)===String(msg.SENDER)
                  &&!msg.PARENT_ID&&!prevMsg.PARENT_ID
                  &&Math.abs((msg.UNIXTIME||0)-(prevMsg.UNIXTIME||0))<120;
  const hasPoll = msg.TYPE==="poll"&&Array.isArray(msg.POLL)&&msg.POLL.length>0;
  const isImg   = msg.TYPE==="image";
  const isVid   = msg.TYPE==="video";
  const paths   = msg.PATH?msg.PATH.split(",").map(p=>p.trim()).filter(Boolean):[];
  const mUrl    = paths.length>0?mediaUrl(paths[0]):null;
  const parsedR = parseReactions(msg.REACTIONS);
  const hasReact= Object.values(parsedR).some(v=>v.count>0);
  const myReact = msg.MY_REACTION||null;

  const [hovered,     setHovered]     = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [toolbarAbove,setToolbarAbove]= useState(true);
  const leaveTimer = useRef(null);
  const bubbleRef  = useRef(null);

  const handleEnter=()=>{
    clearTimeout(leaveTimer.current); setHovered(true);
    if(bubbleRef.current){
      const rect=bubbleRef.current.getBoundingClientRect();
      // Toolbar is ~36px tall — show below if not enough space above
      // DM panel header is ~96px, add toolbar height ~40px = 136px
      setToolbarAbove(rect.top > 140);
    }
  };
  const handleLeave=()=>{ leaveTimer.current=setTimeout(()=>setHovered(false),600); };

  const bg=isMe?color:"#fff";
  const fg=isMe?"#fff":"#111827";

  const handleDelete=async()=>{
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID);
      fd.append("id",msg.ID);
      // Use delete_dm for own messages, delete_dmessage for admin
      const ep=isMe?"/delete_dm":"/delete_dmessage";
      await fetch(`${API}${ep}`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      onDelete(msg.ID);
    }catch(e){}
  };

  return(
    <>
      {lightboxSrc&&<Lightbox src={lightboxSrc} onClose={()=>setLightboxSrc(null)}/>}
      <div id={`dm-${msg.ID}`} style={{display:"flex",flexDirection:"column",
        alignItems:isMe?"flex-end":"flex-start",
        padding:isMe?"1px 8px 1px 36px":"1px 36px 1px 8px",
        marginBottom:"2px"}}>

        {!isMe&&(!prevSame||isReply)&&(
          <div style={{fontSize:"10px",fontWeight:"700",color,marginLeft:"30px",marginBottom:"2px"}}>{name}</div>
        )}

        <div ref={bubbleRef} style={{display:"flex",gap:"4px",alignItems:"flex-end",
          flexDirection:isMe?"row-reverse":"row",maxWidth:"86%"}}
          onMouseEnter={handleEnter} onMouseLeave={handleLeave}>

          {!isMe&&(
            <div style={{flexShrink:0,alignSelf:"flex-end",marginBottom:"2px"}}>
              {(!prevSame||isReply)?<Avatar name={name} color={color} size={26}/>:<div style={{width:26}}/>}
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",
            alignItems:isMe?"flex-end":"flex-start",minWidth:0,maxWidth:"100%",position:"relative"}}>
            {/* Floating toolbar */}
            {hovered&&(
              <div onMouseEnter={handleEnter} onMouseLeave={handleLeave}
                style={{position:"absolute",
                  ...(toolbarAbove?{bottom:"calc(100% + 2px)"}:{top:"calc(100% + 2px)"}),
                  ...(isMe?{right:0}:{left:0}),zIndex:200}}>
                <MessageActions isMe={isMe} color={color} isAdmin={isAdmin}
                  canDelete={isAdmin||isMe}
                  onReply={()=>onReply(msg)}
                  onDelete={handleDelete}
                  user={user} dmId={msg.ID} onReacted={onReacted}/>
              </div>
            )}

            {/* Bubble */}
            <div style={{background:bg,color:fg,
              borderRadius:isMe?"12px 4px 12px 12px":"4px 12px 12px 12px",
              padding:isImg||isVid?"0":hasPoll?"10px 12px":"8px 12px",
              maxWidth:"100%",boxSizing:"border-box",overflow:"hidden",
              boxShadow:"0 1px 2px rgba(0,0,0,0.1)"}}>
              {msg.QUOTED&&<div style={{padding:isImg||isVid?"8px 10px 4px":"0 0 4px"}}>
                <QuotedMsg quoted={msg.QUOTED} color={color} isMe={isMe}/>
              </div>}
              {msg.SHARED&&<div style={{padding:isImg||isVid?"8px 10px 4px":"0 0 4px"}}>
                <SharedDoc shared={msg.SHARED} color={color} isMe={isMe}/>
              </div>}
              {msg.TYPE==="text"&&(
                <div style={{fontSize:"13px",lineHeight:1.5,wordBreak:"break-word"}}>{msg.MESSAGE}</div>
              )}
              {isImg&&mUrl&&(
                <img src={mUrl} alt="" onClick={()=>setLightboxSrc(mUrl)}
                  style={{display:"block",maxWidth:"220px",maxHeight:"180px",
                    objectFit:"cover",cursor:"zoom-in"}}
                  onError={e=>e.target.style.display="none"}/>
              )}
              {isVid&&mUrl&&(
                <video src={mUrl} controls style={{display:"block",maxWidth:"220px",maxHeight:"160px"}}/>
              )}
              {hasPoll&&msg.POLL.map(p=>(
                <PollItem key={p.POLLID} poll={p} msg={msg} user={user} color={color} onAnswer={onReacted}/>
              ))}
              <div style={{fontSize:"9px",color:isMe?"rgba(255,255,255,0.7)":"#9CA3AF",
                textAlign:"right",marginTop:"3px",
                padding:isImg||isVid?"0 8px 6px":"0"}}>{msg.TIMESTAMP}</div>
            </div>

            {hasReact&&(
              <div style={{marginTop:"3px"}}>
                <ReactionPills dmId={msg.ID} reactions={msg.REACTIONS}
                  myReaction={myReact} user={user} color={color}
                  onReacted={onReacted} align={isMe?"right":"left"}/>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Poll builder ─────────────────────────────────────────────────────────────
function PollBuilder({color,onPost,onClose}) {
  const [pollQ,setPollQ]=useState("");
  const [pollType,setPollType]=useState(1);
  const [options,setOptions]=useState(["",""]);
  const [expiry,setExpiry]=useState("");
  const [privacy,setPrivacy]=useState(0);
  const valid=pollQ.trim()&&(pollType===0||options.filter(o=>o.trim()).length>=2);
  return(
    <div style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",
      borderRadius:"16px 16px 0 0",padding:"14px 14px 10px",zIndex:50,
      boxShadow:"0 -6px 24px rgba(0,0,0,0.1)",borderTop:"1px solid #E5E7EB"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
        <span style={{fontSize:"13px",fontWeight:"700",color:"#111"}}>📊 New Poll</span>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:"#9CA3AF",fontSize:"20px",lineHeight:1,padding:0}}>×</button>
      </div>
      <input value={pollQ} onChange={e=>setPollQ(e.target.value)} placeholder="Ask a question…"
        style={{width:"100%",padding:"8px 12px",border:"1px solid #E5E7EB",borderRadius:"8px",
          fontSize:"12px",outline:"none",fontFamily:"inherit",marginBottom:"8px",boxSizing:"border-box"}}/>
      <div style={{display:"flex",gap:"4px",marginBottom:"8px",flexWrap:"wrap",alignItems:"center"}}>
        {[{v:1,l:"Single"},{v:2,l:"Multi"},{v:0,l:"Open"}].map(t=>(
          <button key={t.v} onClick={()=>setPollType(t.v)}
            style={{padding:"3px 10px",borderRadius:"6px",border:"none",cursor:"pointer",
              fontSize:"10px",fontWeight:"600",
              background:pollType===t.v?color:"#F1F5F9",color:pollType===t.v?"#fff":"#6B7280"}}>{t.l}</button>
        ))}
        <button onClick={()=>setPrivacy(p=>p===0?1:0)}
          style={{marginLeft:"auto",padding:"3px 10px",borderRadius:"6px",cursor:"pointer",
            border:`1px solid ${privacy===1?"#6366F1":"#E5E7EB"}`,
            background:privacy===1?"#EEF2FF":"transparent",
            color:privacy===1?"#6366F1":"#6B7280",fontSize:"10px",fontWeight:"600"}}>
          {privacy===1?"🔒 Private":"🌐 Public"}</button>
      </div>
      {pollType!==0&&(
        <div style={{display:"flex",flexDirection:"column",gap:"4px",marginBottom:"8px"}}>
          {options.map((opt,i)=>(
            <div key={i} style={{display:"flex",gap:"4px"}}>
              <input value={opt} onChange={e=>setOptions(o=>{const n=[...o];n[i]=e.target.value;return n;})}
                placeholder={`Option ${i+1}`}
                style={{flex:1,padding:"6px 10px",border:"1px solid #E5E7EB",borderRadius:"7px",
                  fontSize:"11px",outline:"none",fontFamily:"inherit"}}/>
              {options.length>2&&<button onClick={()=>setOptions(o=>o.filter((_,j)=>j!==i))}
                style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",fontSize:"16px",padding:"0 4px"}}>×</button>}
            </div>
          ))}
          {options.length<5&&<button onClick={()=>setOptions(o=>[...o,""])}
            style={{fontSize:"10px",color,background:"none",border:"none",cursor:"pointer",fontWeight:"600",alignSelf:"flex-start"}}>
            + Add option</button>}
        </div>
      )}
      <div style={{display:"flex",gap:"4px",alignItems:"center",marginBottom:"10px"}}>
        <span style={{fontSize:"10px",color:"#9CA3AF"}}>Expires:</span>
        {["15m","1h","4h","24h"].map(e=>(
          <button key={e} onClick={()=>setExpiry(x=>x===e?"":e)}
            style={{padding:"2px 8px",borderRadius:"6px",border:"none",cursor:"pointer",
              fontSize:"10px",fontWeight:"600",
              background:expiry===e?color:"#F1F5F9",color:expiry===e?"#fff":"#6B7280"}}>{e}</button>
        ))}
      </div>
      <div style={{display:"flex",gap:"6px"}}>
        <button onClick={()=>{setPollQ("");setOptions(["",""]);setExpiry("");setPrivacy(0);setPollType(1);}}
          style={{flex:1,padding:"9px",border:"1px solid #E5E7EB",borderRadius:"9px",
            background:"#fff",color:"#6B7280",fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>Clear</button>
        <button onClick={()=>onPost({pollQ,pollType,options:options.filter(o=>o.trim()),expiry,privacy})}
          disabled={!valid}
          style={{flex:2,padding:"9px",border:"none",borderRadius:"9px",background:color,
            color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer",opacity:!valid?"0.5":"1"}}>
          Post Poll</button>
      </div>
    </div>
  );
}

// ─── Plus menu ────────────────────────────────────────────────────────────────
function DMPlusMenu({color,onPoll,onPhoto,onVideo}) {
  const [open,setOpen]=useState(false);
  const ref=useRef();
  useEffect(()=>{
    if(!open) return;
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[open]);
  return(
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(s=>!s)}
        style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
          background:"transparent",cursor:"pointer",fontSize:"24px",color:"#9CA3AF",
          display:"flex",alignItems:"center",justifyContent:"center",
          transition:"transform 0.2s",transform:open?"rotate(45deg)":"none"}}>+</button>
      {open&&(
        <div style={{position:"absolute",bottom:"44px",right:0,background:"#fff",
          borderRadius:"14px",padding:"5px",boxShadow:"0 -2px 20px rgba(0,0,0,0.12)",
          border:"1px solid #E5E7EB",minWidth:"160px",zIndex:500}}>
          {[
            {icon:"📊",label:"Poll",  bg:"#EEF2FF",fn:()=>{setOpen(false);onPoll();}},
            {icon:"📷",label:"Photo", bg:"#ECFDF5",fn:()=>{setOpen(false);onPhoto();}},
            {icon:"🎬",label:"Video", bg:"#FEF2F2",fn:()=>{setOpen(false);onVideo();}},
          ].map(({icon,label,bg,fn})=>(
            <button key={label} onClick={fn}
              style={{display:"flex",alignItems:"center",gap:"10px",width:"100%",
                padding:"8px 12px",border:"none",background:"none",cursor:"pointer",borderRadius:"10px"}}
              onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              <div style={{width:"34px",height:"34px",borderRadius:"8px",background:bg,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:"17px",flexShrink:0}}>
                {icon}
              </div>
              <span style={{fontSize:"12px",color:"#374151",fontWeight:"600"}}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Group info screen ────────────────────────────────────────────────────────
function GroupInfo({group,user,activeSub,color,onBack,isAdmin}) {
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showAdd,   setShowAdd]   = useState(false);
  const [allUsers,  setAllUsers]  = useState([]);
  const [search,    setSearch]    = useState("");
  const [selected,  setSelected]  = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [addErr,    setAddErr]    = useState("");
  const [loadingU,  setLoadingU]  = useState(false);

  useEffect(()=>{
    const load=async()=>{
      try{
        const r=await fetch(
          `${API}/DM_Group_Users?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}&groupid=${group.GROUPID||group.DMID}`,
          {headers:{"Auth-Code":user.AUTH_CODE}});
        const res=await r.json();
        if(String(res.STATUS)==="1") setMembers(res.DATA||[]);
      }catch(e){}
      setLoading(false);
    };
    load();
  },[group.GROUPID]);

  const openAddUser = async () => {
    setShowAdd(true); setSearch(""); setSelected([]); setAddErr("");
    if(allUsers.length) return;
    setLoadingU(true);
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID);
      fd.append("subprojectid",activeSub.SUBPROJECTID);
      const r=await fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1") setAllUsers(res.DATA||[]);
    }catch(e){}
    setLoadingU(false);
  };

  const addUsers = async () => {
    if(!selected.length) return;
    setAdding(true); setAddErr("");
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID);
      fd.append("subprojectid",activeSub.SUBPROJECTID);
      fd.append("groupid",group.GROUPID||group.DMID);
      selected.forEach((u,i)=>fd.append(`users[${i}]`,u.USERID));
      const r=await fetch(`${API}/Add_User_to_Group`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1"){
        setMembers(res.DATA||[]);
        setShowAdd(false); setSelected([]);
      } else setAddErr(res.MESSAGE||"Failed to add users");
    }catch(e){setAddErr("Network error");}
    setAdding(false);
  };

  const removeUser=async uid=>{
    try{
      const fd=new FormData();
      fd.append("userid",user.USERID);
      fd.append("subprojectid",activeSub.SUBPROJECTID);
      fd.append("groupid",group.GROUPID||group.DMID);
      fd.append("users[]",uid);
      const r=await fetch(`${API}/Delete_Dm_Group_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1") setMembers(m=>m.filter(x=>x.USERID!==uid));
    }catch(e){}
  };

  // Filter out existing members from user list
  const memberIds = new Set(members.map(m=>String(m.USERID)));
  const filtered  = allUsers
    .filter(u=>!memberIds.has(String(u.USERID)))
    .filter(u=>!search||u.NAME?.toLowerCase().includes(search.toLowerCase())||u.EMAIL?.toLowerCase().includes(search.toLowerCase()));

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"#fff"}}>
      <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"10px",
        padding:"10px 14px",background:color,color:"#fff"}}>
        <button onClick={showAdd?()=>{setShowAdd(false);setSelected([]);}:onBack}
          style={{background:"rgba(255,255,255,0.2)",border:"none",
          cursor:"pointer",color:"#fff",borderRadius:"50%",width:"28px",height:"28px",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px",flexShrink:0}}>←</button>
        <span style={{fontSize:"14px",fontWeight:"700",flex:1}}>
          {showAdd?"Add Members":"Group Info"}
        </span>
        {isAdmin&&group.DM_TYPE==1&&!showAdd&&(
          <button onClick={openAddUser}
            style={{background:"rgba(255,255,255,0.2)",border:"none",cursor:"pointer",
              color:"#fff",borderRadius:"6px",padding:"4px 10px",fontSize:"12px",fontWeight:"700"}}>
            + Add
          </button>
        )}
      </div>

      {/* Add User picker */}
      {showAdd&&(
        <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid #F3F4F6",flexShrink:0}}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search users…"
              style={{width:"100%",boxSizing:"border-box",padding:"7px 12px",
                border:"1px solid #E5E7EB",borderRadius:"8px",fontSize:"13px",
                outline:"none",fontFamily:"inherit"}}/>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
            {loadingU&&<div style={{textAlign:"center",padding:"20px",color:"#9CA3AF",fontSize:"12px"}}>Loading…</div>}
            {filtered.map(u=>{
              const sel=selected.find(x=>x.USERID===u.USERID);
              return(
                <div key={u.USERID}
                  onClick={()=>setSelected(s=>sel?s.filter(x=>x.USERID!==u.USERID):[...s,u])}
                  style={{display:"flex",alignItems:"center",gap:"10px",padding:"8px 14px",
                    cursor:"pointer",background:sel?`${color}08`:"#fff",
                    borderBottom:"1px solid #F9FAFB"}}>
                  <Avatar name={u.NAME} color={color} size={34}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"13px",fontWeight:"600",color:"#111"}}>{u.NAME}</div>
                    <div style={{fontSize:"10px",color:"#9CA3AF"}}>{u.EMAIL}</div>
                  </div>
                  {sel&&<div style={{width:"18px",height:"18px",borderRadius:"50%",
                    background:color,display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"11px",color:"#fff",flexShrink:0}}>✓</div>}
                </div>
              );
            })}
          </div>
          {addErr&&<div style={{padding:"8px 14px",fontSize:"12px",color:"#EF4444",flexShrink:0}}>{addErr}</div>}
          {selected.length>0&&(
            <div style={{padding:"10px 14px",borderTop:"1px solid #F3F4F6",flexShrink:0}}>
              <button onClick={addUsers} disabled={adding}
                style={{width:"100%",padding:"10px",borderRadius:"8px",border:"none",
                  background:color,color:"#fff",fontSize:"13px",fontWeight:"700",
                  cursor:adding?"not-allowed":"pointer",opacity:adding?0.7:1}}>
                {adding?"Adding…":`Add ${selected.length} Member${selected.length>1?"s":""}`}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Members list */}
      {!showAdd&&<div style={{flex:1,overflowY:"auto",padding:"16px"}}>
        <div style={{textAlign:"center",marginBottom:"20px"}}>
          <div style={{width:"60px",height:"60px",borderRadius:"50%",background:`${color}22`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:"22px",fontWeight:"800",color,margin:"0 auto 8px"}}>
            {(group.GROUPNAME||"G").slice(0,2).toUpperCase()}
          </div>
          <div style={{fontSize:"15px",fontWeight:"700",color:"#111"}}>{group.GROUPNAME}</div>
          <div style={{fontSize:"11px",color:"#9CA3AF",marginTop:"2px"}}>
            {group.DM_TYPE===1?"Group DM":"Direct Message"}
          </div>
        </div>
        <div style={{fontSize:"11px",fontWeight:"700",color:"#9CA3AF",marginBottom:"8px",textTransform:"uppercase",letterSpacing:"0.5px"}}>
          Members ({members.length})
        </div>
        {loading&&<div style={{textAlign:"center",padding:"20px",color:"#9CA3AF",fontSize:"12px"}}>Loading…</div>}
        {members.map(m=>(
          <div key={m.USERID} style={{display:"flex",alignItems:"center",gap:"10px",
            padding:"8px 0",borderBottom:"1px solid #F3F4F6"}}>
            <Avatar name={m.NAME} color={color} size={36}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"13px",fontWeight:"600",color:"#111"}}>{m.NAME}</div>
              <div style={{fontSize:"10px",color:"#9CA3AF"}}>{m.EMAIL}</div>
              <div style={{fontSize:"10px",color:"#9CA3AF"}}>Last seen: {m.VIEWED||"⌛"}</div>
            </div>
            {isAdmin&&String(m.USERID)!==String(user.USERID)&&(
              <button onClick={()=>removeUser(m.USERID)}
                style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:"6px",
                  padding:"4px 8px",cursor:"pointer",color:"#EF4444",fontSize:"10px",fontWeight:"600"}}>
                Remove
              </button>
            )}
          </div>
        ))}
      </div>}
    </div>
  );
}

// ─── New conversation screen ──────────────────────────────────────────────────
function NewDM({user,activeSub,color,onBack,onCreated}) {
  const [search,    setSearch]   = useState("");
  const [selected,  setSelected] = useState([]);
  const [gname,     setGname]    = useState("");
  const [message,   setMessage]  = useState("");
  const [sending,   setSending]  = useState(false);
  const [users,     setUsers]    = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState("");
  // Filters
  const [filterMode,setFilterMode]= useState("all"); // all | group | congress
  const [groups,    setGroups]   = useState([]);
  const [selGroup,  setSelGroup] = useState(null);
  const [bisLoaded, setBisLoaded]= useState(false);
  const groupsRef   = useRef([]);  // sync ref — always current

  // Fetch BIS groups — called on demand when By Group tab is clicked
  const fetchBisAndLoad = () => {
    setLoading(true); setError(""); setUsers([]);
    fetch(`${API}/bis?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
    .then(r=>r.json())
    .then(res=>{
      const grps=Array.isArray(res.GROUP)?res.GROUP:Array.isArray(res.DATA?.GROUP)?res.DATA.GROUP:[];
      if(!grps.length){ setLoading(false); setError("No groups found"); return; }
      groupsRef.current=grps;
      setGroups(grps);
      setBisLoaded(true);
      const primary=grps.find(g=>g.PRIMARY_GRP==="YES")||grps[0];
      setSelGroup(primary);
      // Build FormData with ALL groupids and post to DM_Users
      const fd=new FormData();
      fd.append("userid",        user.USERID);
      fd.append("subprojectid",  activeSub.SUBPROJECTID);
      grps.forEach(g=>fd.append("groupid", String(g.GROUPID)));
      fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
      .then(r2=>r2.json())
      .then(res2=>{
        if(String(res2.STATUS)==="1"&&Array.isArray(res2.DATA)&&res2.DATA.length>0){
          setUsers(res2.DATA);
        } else { setUsers([]); setError(res2.MESSAGE||"No users found"); }
        setLoading(false);
      })
      .catch(()=>{ setLoading(false); setError("Failed to load users"); });
    })
    .catch(()=>{ setLoading(false); setError("Failed to load groups"); });
  };

  // Load users from /DM_Users with correct params
  // Direct group load — takes array of groupids, posts to DM_Users
  const loadUsersWithGroup=async(groupIds)=>{
    setLoading(true); setError(""); setUsers([]);
    try{
      const fd=new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub.SUBPROJECTID);
      groupIds.forEach(id=>fd.append("groupid", String(id)));
      const r=await fetch(`${API}/DM_Users`,{
        method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd
      });
      const res=await r.json();
      if(String(res.STATUS)==="1"&&Array.isArray(res.DATA)&&res.DATA.length>0){
        setUsers(res.DATA);
      } else { setUsers([]); setError(res.MESSAGE||"No users found"); }
    }catch(e){ setError("Failed to load users"); }
    setLoading(false);
  };

  const loadUsers=async(mode, groupIds)=>{
    setLoading(true); setError(""); setUsers([]);
    try{
      const fd=new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub.SUBPROJECTID);
      if(mode==="congress") fd.append("congress","1");
      const r=await fetch(`${API}/DM_Users`,{
        method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd
      });
      const res=await r.json();
      if(String(res.STATUS)==="1"&&Array.isArray(res.DATA)&&res.DATA.length>0){
        setUsers(res.DATA);
      } else { setUsers([]); setError(res.MESSAGE||"No users found"); }
    }catch(e){ setError("Failed to load users"); }
    setLoading(false);
  };

  // Initial load — all users
  useEffect(()=>{ loadUsers("all",null); },[]);

  const switchFilter=(mode)=>{
    setFilterMode(mode);
    setSearch("");
    setError("");
    if(mode==="all")      { loadUsers("all",null); return; }
    if(mode==="congress") { loadUsers("congress",null); return; }
    if(mode==="group"){
      // Always fetch BIS fresh to get groups, then load users with groupid
      fetchBisAndLoad();
    }
  };

  const selectGroup=(g)=>{
    setSelGroup(g);
    setLoading(true); setError(""); setUsers([]);
    const fd=new FormData();
    fd.append("userid",       user.USERID);
    fd.append("subprojectid", activeSub.SUBPROJECTID);
    fd.append("groupid",      String(g.GROUPID));
    fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
    .then(r=>r.json())
    .then(res=>{
      if(String(res.STATUS)==="1"&&Array.isArray(res.DATA)&&res.DATA.length>0){
        setUsers(res.DATA);
      } else { setUsers([]); setError(res.MESSAGE||"No users found"); }
      setLoading(false);
    })
    .catch(()=>{ setLoading(false); setError("Failed to load users"); });
  };

  const toggleUser=(u)=>setSelected(s=>
    s.find(x=>x.USERID===u.USERID)
      ?s.filter(x=>x.USERID!==u.USERID)
      :[...s,u]
  );

  const create=async()=>{
    if(!message.trim()||!selected.length) return;
    setSending(true);
    try{
      const fd=new FormData();
      fd.append("sender",      user.USERID);
      fd.append("subprojectid",activeSub.SUBPROJECTID);
      fd.append("message",     message.trim());
      if(selected.length>1&&gname.trim()) fd.append("gname",gname.trim());
      selected.forEach(u=>fd.append("receiver[]",u.USERID));
      const r=await fetch(`${API}/DM`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1") onCreated();
      else setError(res.MESSAGE||"Failed to send");
    }catch(e){setError("Network error");}
    setSending(false);
  };

  const filtered=users.filter(u=>
    (u.NAME||"").toLowerCase().includes(search.toLowerCase())
  );
  const hasGroups=filtered.some(u=>u.USER_GROUPNAME);
  const grouped=hasGroups
    ?filtered.reduce((acc,u)=>{
        const g=u.USER_GROUPNAME||"Other";
        if(!acc[g]) acc[g]=[];
        acc[g].push(u);
        return acc;
      },{})
    :null;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"#fff"}}>
      {/* Header */}
      <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"10px",
        padding:"10px 14px",background:color,color:"#fff"}}>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.2)",border:"none",
          cursor:"pointer",color:"#fff",borderRadius:"50%",width:"28px",height:"28px",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px",flexShrink:0}}>←</button>
        <span style={{fontSize:"14px",fontWeight:"700",flex:1}}>New Message</span>
        {selected.length>0&&<span style={{fontSize:"11px",opacity:0.8}}>{selected.length} selected</span>}
      </div>

      {/* Selected chips */}
      {selected.length>0&&(
        <div style={{flexShrink:0,padding:"8px 12px",borderBottom:"1px solid #E5E7EB",
          display:"flex",gap:"6px",flexWrap:"wrap",background:"#FAFAFA"}}>
          {selected.map(u=>(
            <div key={u.USERID} style={{background:`${color}15`,border:`1px solid ${color}33`,
              borderRadius:"20px",padding:"3px 10px",fontSize:"11px",color,
              display:"flex",alignItems:"center",gap:"4px",fontWeight:"600"}}>
              {u.NAME}
              <button onClick={()=>toggleUser(u)}
                style={{background:"none",border:"none",cursor:"pointer",
                  color,fontSize:"14px",lineHeight:1,padding:0}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Group name — 2+ selected */}
      {selected.length>1&&(
        <div style={{flexShrink:0,padding:"6px 12px",borderBottom:"1px solid #E5E7EB"}}>
          <input value={gname} onChange={e=>setGname(e.target.value)}
            placeholder="Group name (optional)…"
            style={{width:"100%",padding:"7px 12px",border:"1px solid #E5E7EB",borderRadius:"8px",
              fontSize:"12px",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
        </div>
      )}

      {/* 3 Filter tabs */}
      <div style={{flexShrink:0,display:"flex",borderBottom:"2px solid #F3F4F6",background:"#fff"}}>
        {[
          {id:"all",     label:"All Users"},
          {id:"group",   label:"By Group"},
          {id:"congress",label:"Congress"},
        ].map(f=>(
          <button key={f.id} onClick={()=>switchFilter(f.id)}
            style={{flex:1,padding:"9px 4px",border:"none",cursor:"pointer",
              fontSize:"11px",fontWeight:"700",background:"none",
              color:filterMode===f.id?color:"#9CA3AF",
              borderBottom:`2px solid ${filterMode===f.id?color:"transparent"}`,
              marginBottom:"-2px",transition:"all 0.15s"}}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Group picker — only shown when group filter active */}
      {filterMode==="group"&&groups.length>0&&(
        <div style={{flexShrink:0,padding:"8px 12px",borderBottom:"1px solid #F3F4F6",
          display:"flex",gap:"6px",flexWrap:"wrap",background:"#FAFAFA"}}>
          {groups.map(g=>(
            <button key={g.GROUPID} onClick={()=>selectGroup(g)}
              style={{padding:"4px 12px",borderRadius:"20px",cursor:"pointer",fontSize:"11px",
                fontWeight:"600",border:`1px solid ${selGroup?.GROUPID===g.GROUPID?color:"#E5E7EB"}`,
                background:selGroup?.GROUPID===g.GROUPID?`${color}15`:"#fff",
                color:selGroup?.GROUPID===g.GROUPID?color:"#6B7280",
                transition:"all 0.12s"}}>
              {g.GROUPNAME}
            </button>
          ))}
        </div>
      )}
      {filterMode==="group"&&bisLoaded&&groups.length===0&&(
        <div style={{flexShrink:0,padding:"8px 12px",fontSize:"11px",color:"#9CA3AF",
          textAlign:"center",borderBottom:"1px solid #F3F4F6"}}>
          No groups found in BIS
          <button onClick={()=>{
            setBisLoaded(false);
            fetch(`${API}/bis?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
              {headers:{"Auth-Code":user.AUTH_CODE}})
              .then(r=>r.json()).then(res=>{
                console.log("[BIS retry]",res);
                const rg=res.GROUP||res.DATA?.GROUP; if(Array.isArray(rg)&&rg.length>0){ const res2={...res,GROUP:rg}; res=res2;
                  const grps=res.GROUP;
                  groupsRef.current=grps;
                  window.__dmGroups=grps;
                  setGroups(grps);
                  const p=grps.find(g=>g.PRIMARY_GRP==="YES")||grps[0];
                  setSelGroup(p); setBisLoaded(true);
                  loadUsersWithGroup([p.GROUPID]);
                } else setBisLoaded(true);
              }).catch(e=>{console.error(e);setBisLoaded(true);});
          }} style={{marginLeft:"8px",padding:"2px 8px",border:`1px solid #E5E7EB`,
            borderRadius:"6px",background:"#fff",color:"#374151",fontSize:"10px",cursor:"pointer"}}>
            Retry
          </button>
        </div>
      )}
      {filterMode==="group"&&!bisLoaded&&groups.length===0&&(
        <div style={{flexShrink:0,padding:"8px 12px",fontSize:"11px",color:"#9CA3AF",
          textAlign:"center",borderBottom:"1px solid #F3F4F6"}}>Loading groups…</div>
      )}

      {/* Search + Select All */}
      <div style={{flexShrink:0,padding:"8px 12px",borderBottom:"1px solid #F3F4F6",
        display:"flex",gap:"8px",alignItems:"center"}}>
        <div style={{flex:1,display:"flex",alignItems:"center",gap:"8px",background:"#F3F4F6",
          borderRadius:"8px",padding:"7px 12px"}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="#9CA3AF" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search people…"
            style={{flex:1,border:"none",background:"transparent",fontSize:"12px",
              outline:"none",fontFamily:"inherit"}}/>
          {search&&<button onClick={()=>setSearch("")}
            style={{background:"none",border:"none",cursor:"pointer",
              color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:0}}>×</button>}
        </div>
        {/* Select All / Deselect All */}
        {filtered.length>0&&(()=>{
          const allSelected=filtered.every(u=>selected.find(x=>x.USERID===u.USERID));
          return(
            <button
              onClick={()=>{
                if(allSelected){
                  // Deselect all filtered
                  const filteredIds=new Set(filtered.map(u=>u.USERID));
                  setSelected(s=>s.filter(x=>!filteredIds.has(x.USERID)));
                } else {
                  // Add all filtered not already selected
                  const toAdd=filtered.filter(u=>!selected.find(x=>x.USERID===u.USERID));
                  setSelected(s=>[...s,...toAdd]);
                }
              }}
              style={{flexShrink:0,padding:"6px 10px",borderRadius:"8px",cursor:"pointer",
                fontSize:"11px",fontWeight:"700",whiteSpace:"nowrap",
                border:`1px solid ${color}44`,
                background:allSelected?`${color}15`:"#fff",
                color:allSelected?color:"#6B7280",
                transition:"all 0.15s"}}>
              {allSelected?"✓ All":"Select All"}
            </button>
          );
        })()}
      </div>

      {/* User list */}
      <div style={{flex:1,overflowY:"auto"}}>
        {loading&&(
          <div style={{textAlign:"center",padding:"30px",color:"#9CA3AF",fontSize:"12px"}}>
            Loading users…
          </div>
        )}
        {!loading&&filterMode==="group"&&!selGroup&&(
          <div style={{textAlign:"center",padding:"30px",color:"#9CA3AF",fontSize:"12px"}}>
            Select a group above to see its members
          </div>
        )}
        {!loading&&error&&!users.length&&(
          <div style={{textAlign:"center",padding:"30px",color:"#9CA3AF",fontSize:"12px"}}>
            {error}
          </div>
        )}
        {!loading&&!!users.length&&!filtered.length&&(
          <div style={{textAlign:"center",padding:"20px",color:"#9CA3AF",fontSize:"12px"}}>
            No users match "{search}"
          </div>
        )}

        {/* Grouped */}
        {!loading&&grouped&&Object.entries(grouped).map(([grpName,members])=>(
          <div key={grpName}>
            <div style={{padding:"7px 14px 3px",fontSize:"10px",fontWeight:"700",
              color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px",
              background:"#F8FAFC",borderBottom:"1px solid #F3F4F6"}}>{grpName}</div>
            {members.map(u=>UserRow({u,sel:selected.find(x=>x.USERID===u.USERID),color,toggle:toggleUser}))}
          </div>
        ))}

        {/* Flat */}
        {!loading&&!grouped&&filtered.map(u=>
          UserRow({u,sel:selected.find(x=>x.USERID===u.USERID),color,toggle:toggleUser})
        )}
      </div>

      {/* Message + Send */}
      {selected.length>0&&(
        <div style={{flexShrink:0,padding:"10px 12px",borderTop:"1px solid #E5E7EB",background:"#fff"}}>
          {error&&<div style={{fontSize:"11px",color:"#EF4444",marginBottom:"6px",textAlign:"center"}}>{error}</div>}
          <div style={{display:"flex",gap:"8px",alignItems:"flex-end"}}>
            <div style={{flex:1,background:"#F3F4F6",borderRadius:"10px",padding:"8px 12px",minWidth:0}}>
              <textarea value={message} onChange={e=>setMessage(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();create();}}}
                placeholder={`Message ${selected.length===1?selected[0].NAME:`${selected.length} people`}…`}
                rows={1}
                style={{width:"100%",border:"none",background:"transparent",fontSize:"12px",
                  fontFamily:"inherit",resize:"none",outline:"none",lineHeight:1.4,
                  maxHeight:"60px",overflowY:"auto"}}/>
            </div>
            <button onClick={create} disabled={sending||!message.trim()}
              style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
                background:message.trim()?color:"#E5E7EB",color:"#fff",cursor:"pointer",
                flexShrink:0,fontSize:"14px",display:"flex",alignItems:"center",
                justifyContent:"center",transition:"all 0.15s",
                boxShadow:message.trim()?"0 2px 8px rgba(0,0,0,0.2)":"none"}}>
              {sending
                ?<div style={{width:"12px",height:"12px",border:"2px solid rgba(255,255,255,0.4)",
                    borderTopColor:"#fff",borderRadius:"50%",animation:"_sp 0.7s linear infinite"}}/>
                :"➤"}
              <style>{"@keyframes _sp{to{transform:rotate(360deg)}}"}</style>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable user row
function UserRow({u,sel,color,toggle}) {
  return(
    <div key={u.USERID} onClick={()=>toggle(u)}
      style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",
        cursor:"pointer",borderBottom:"1px solid #F9FAFB",
        background:sel?`${color}08`:"transparent",transition:"background 0.1s"}}
      onMouseEnter={e=>!sel&&(e.currentTarget.style.background="#F8FAFC")}
      onMouseLeave={e=>!sel&&(e.currentTarget.style.background=sel?`${color}08`:"transparent")}>
      <div style={{width:"20px",height:"20px",borderRadius:"5px",flexShrink:0,
        border:`2px solid ${sel?color:"#D1D5DB"}`,background:sel?color:"#fff",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        {sel&&<span style={{color:"#fff",fontSize:"11px",fontWeight:"900",lineHeight:1}}>✓</span>}
      </div>
      <Avatar name={u.NAME} color={color} size={32}/>
      <span style={{fontSize:"13px",color:"#111",fontWeight:"500",flex:1}}>{u.NAME}</span>
    </div>
  );
}


// ─── DM Chat ──────────────────────────────────────────────────────────────────
function DMChat({group,user,activeSub,color,onBack,onClose}) {
  const isAdmin=activeSub?.ROLE===1;
  const [messages,  setMessages]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [hasMore,   setHasMore]   = useState(false);
  const [offset,    setOffset]    = useState(0);
  const [replyingTo,setReplyingTo]= useState(null);
  const [text,      setText]      = useState("");
  const [sending,   setSending]   = useState(false);
  const [showPoll,  setShowPoll]  = useState(false);
  const [imgFile,   setImgFile]   = useState(null);
  const [imgPrev,   setImgPrev]   = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showInfo,  setShowInfo]  = useState(false);
  const listRef = useRef();
  const fileRef = useRef();
  const textRef = useRef();
  const emojiRef= useRef();
  const SMILEYS=["😀","😂","😍","🥰","😎","🤔","😢","😡","👏","🙏","🔥","💯","✅","❤️","🎉","👍","👎","🤝"];

  const scrollToBottom=()=>{ if(listRef.current) listRef.current.scrollTop=listRef.current.scrollHeight; };

  const [dmType, setDmType] = useState(group.DM_TYPE??null);

  const load=async(reset=false)=>{
    setLoading(true);
    try{
      const o=reset?0:offset;
      const r=await fetch(
        `${API}/common_get_dm_data?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}&groupid=${group.GROUPID||group.DMID}&first=${o}&last=${PAGE}`,
        {headers:{"Auth-Code":user.AUTH_CODE}});
      const res=await r.json();
      if((res.STATUS==="1"||res.STATUS===1)&&Array.isArray(res.DATA)&&res.DATA.length>0){
        const data=[...res.DATA].reverse();
        setMessages(prev=>reset?data:[...data,...prev]);
        setHasMore(res.DATA.length===PAGE);
        setOffset(o+PAGE);
        // extract DM_TYPE from first message
        if(res.DATA[0]?.DM_TYPE!=null) setDmType(res.DATA[0].DM_TYPE);
      }else{setHasMore(false);if(reset)setMessages([]);}
    }catch(e){setHasMore(false);}
    setLoading(false);
  };

  useEffect(()=>{ load(true); },[group.GROUPID]);
  useEffect(()=>{ if(!loading&&offset===PAGE) scrollToBottom(); },[loading]);

  useEffect(()=>{
    if(!showEmoji) return;
    const h=e=>{if(emojiRef.current&&!emojiRef.current.contains(e.target))setShowEmoji(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[showEmoji]);

  // Listen for WS events from parent DMPanel
  useEffect(()=>{
    const onNew=(e)=>{
      const msg=e.detail.message;
      if(!msg) return;
      const msgGroup=String(msg.GROUPID||msg.DMID||msg.groupid||"");
      const thisGroup=String(group.GROUPID||group.DMID||"");
      if(msgGroup&&thisGroup&&msgGroup!==thisGroup) return;
      const id=msg.ID||msg.id;
      if(id){
        setMessages(prev=>{
          if(prev.find(m=>(m.ID||m.id)===id)) return prev;
          if(listRef.current){
            const{scrollTop,scrollHeight,clientHeight}=listRef.current;
            if(scrollHeight-scrollTop-clientHeight<120)
              setTimeout(()=>{if(listRef.current)listRef.current.scrollTop=listRef.current.scrollHeight;},80);
          }
          return [...prev,msg];
        });
      } else { load(true); setTimeout(scrollToBottom,500); }
    };
    const onReact=(e)=>{
      const{dm_id,reactions}=e.detail;
      setMessages(prev=>prev.map(m=>m.ID===dm_id?{...m,REACTIONS:reactions}:m));
    };
    const onDel=(e)=>{
      const{id}=e.detail;
      setMessages(prev=>prev.filter(m=>m.ID!==id&&String(m.ID)!==String(id)));
    };
    window.addEventListener("dm_new_message",onNew);
    window.addEventListener("dm_new_reaction",onReact);
    window.addEventListener("dm_delete_message",onDel);
    return()=>{
      window.removeEventListener("dm_new_message",onNew);
      window.removeEventListener("dm_new_reaction",onReact);
      window.removeEventListener("dm_delete_message",onDel);
    };
  },[group.GROUPID]);

  const send=async(override=null,extra=null)=>{
    if(sending) return;
    setSending(true);
    try{
      const fd=new FormData();
      fd.append("sender",user.USERID);
      fd.append("groupid",group.GROUPID||group.DMID);
      fd.append("privacy","0");
      if(replyingTo) fd.append("parent_id",replyingTo.ID);

      if(extra?.type==="poll"){
        const{pollQ,pollType,options,expiry,privacy}=extra;
        fd.set("privacy",String(privacy));
        fd.append("type","poll"); fd.append("message",pollQ);
        fd.append("poll",JSON.stringify({poll_question:pollQ,mcq:pollType,options:pollType===0?[]:options}));
        if(expiry){const map={"15m":15*60,"1h":3600,"4h":4*3600,"24h":24*3600};
          const s=map[expiry];if(s)fd.append("expiretime",new Date(Date.now()+s*1000).toISOString().slice(0,19));}
        setShowPoll(false);
      } else if(override){
        fd.append("type",override.type); fd.append("media",override.file);
      } else {
        if(!text.trim()){setSending(false);return;}
        fd.append("type","text"); fd.append("message",text.trim());
      }

      const r=await fetch(`${API}/D_Message`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(String(res.STATUS)==="1"){
        setText(""); setImgFile(null); setImgPrev(null);
        if(fileRef.current) fileRef.current.value="";
        setReplyingTo(null); load(true);
        setTimeout(scrollToBottom,300);
        textRef.current?.focus();
      }
    }catch(e){}
    setSending(false);
  };

  const handleDelete=(id)=>{
    setMessages(m=>m.filter(x=>x.ID!==id&&String(x.ID)!==String(id)));
  };

  const handleReacted=()=>{ load(true); };

  if(showInfo) return(
    <GroupInfo group={{...group, DM_TYPE:dmType}} user={user} activeSub={activeSub}
      color={color} onBack={()=>setShowInfo(false)} isAdmin={isAdmin}/>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",
      background:"#EBE5DE",position:"relative"}}>
      {/* Header */}
      <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"8px",
        padding:"8px 12px",background:color,color:"#fff"}}>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.2)",border:"none",
          cursor:"pointer",color:"#fff",borderRadius:"50%",width:"28px",height:"28px",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px",flexShrink:0}}>←</button>
        <button onClick={()=>setShowInfo(true)}
          style={{background:"none",border:"none",cursor:"pointer",padding:0,display:"flex",
            alignItems:"center",gap:"8px",flex:1,minWidth:0,color:"#fff",textAlign:"left"}}>
          <Avatar name={group.GROUPNAME} color="rgba(255,255,255,0.4)" size={32}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:"13px",fontWeight:"700",overflow:"hidden",
              textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{group.GROUPNAME}</div>
            <div style={{fontSize:"10px",opacity:0.8}}>
              {group.DM_TYPE===1?"Group · tap for info":"Direct Message · tap for info"}
            </div>
          </div>
        </button>
        <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",border:"none",
          cursor:"pointer",color:"#fff",borderRadius:"50%",width:"28px",height:"28px",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px",flexShrink:0}}>×</button>
      </div>

      {/* Image preview */}
      {imgPrev&&(
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.9)",zIndex:100,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"14px"}}>
          <img src={imgPrev} alt="" style={{maxWidth:"88%",maxHeight:"55%",borderRadius:"12px",objectFit:"contain"}}/>
          <div style={{display:"flex",gap:"10px"}}>
            <button onClick={()=>{setImgFile(null);setImgPrev(null);if(fileRef.current)fileRef.current.value="";}}
              style={{padding:"8px 22px",borderRadius:"9px",border:"1px solid rgba(255,255,255,0.3)",
                background:"transparent",color:"#fff",fontSize:"12px",cursor:"pointer"}}>Cancel</button>
            <button onClick={()=>send({type:"image",file:imgFile})} disabled={sending}
              style={{padding:"8px 28px",borderRadius:"9px",border:"none",background:"#fff",
                color:"#111",fontSize:"12px",fontWeight:"700",cursor:"pointer"}}>
              {sending?"Sending…":"Send"}</button>
          </div>
        </div>
      )}

      {/* Poll builder */}
      {showPoll&&<PollBuilder color={color} onClose={()=>setShowPoll(false)}
        onPost={data=>send(null,{type:"poll",...data})}/>}

      {/* Messages */}
      <div ref={listRef} style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:"6px 0 4px",position:"relative"}}>
        {hasMore&&!loading&&(
          <div style={{textAlign:"center",margin:"8px 0"}}>
            <button onClick={()=>load(false)}
              style={{padding:"4px 16px",border:"none",borderRadius:"20px",
                background:"rgba(255,255,255,0.85)",color:"#374151",
                fontSize:"11px",cursor:"pointer",fontWeight:"600",
                boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}>↑ Load older</button>
          </div>
        )}
        {loading&&<div style={{textAlign:"center",padding:"30px",color:"#777",fontSize:"12px"}}>Loading…</div>}
        {!loading&&messages.length===0&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:"36px",marginBottom:"8px",opacity:0.3}}>💬</div>
            <div style={{fontSize:"12px",color:"#888"}}>No messages yet — say hello!</div>
          </div>
        )}
        {messages.map((m,i)=>(
          <DMBubble key={m.ID} msg={m} prevMsg={messages[i-1]||null}
            user={user} color={color} isAdmin={isAdmin}
            onDelete={handleDelete} onReacted={handleReacted}
            onReply={msg=>setReplyingTo(msg)}/>
        ))}
      </div>

      {/* Input */}
      <div style={{flexShrink:0,background:"#EBE5DE",position:"relative"}}>
        {replyingTo&&<ReplyBar msg={replyingTo} color={color} onCancel={()=>setReplyingTo(null)}/>}
        <div style={{display:"flex",alignItems:"flex-end",gap:"6px",padding:"6px 8px"}}>
          <div style={{position:"relative"}} ref={emojiRef}>
            <button onClick={()=>setShowEmoji(s=>!s)}
              style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
                background:"transparent",cursor:"pointer",fontSize:"22px",
                display:"flex",alignItems:"center",justifyContent:"center",color:"#9CA3AF",flexShrink:0}}>
              😊
            </button>
            {showEmoji&&(
              <div style={{position:"absolute",bottom:"44px",left:0,background:"#fff",
                borderRadius:"12px",padding:"8px",boxShadow:"0 4px 20px rgba(0,0,0,0.12)",
                border:"1px solid #E5E7EB",display:"grid",gridTemplateColumns:"repeat(6,1fr)",
                gap:"2px",zIndex:300,width:"192px"}}>
                {SMILEYS.map(e=>(
                  <button key={e} onClick={()=>{setText(t=>t+e);setShowEmoji(false);textRef.current?.focus();}}
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:"18px",
                      padding:"3px",borderRadius:"6px",lineHeight:1}}
                    onMouseEnter={ev=>ev.currentTarget.style.background="#F3F4F6"}
                    onMouseLeave={ev=>ev.currentTarget.style.background="none"}>{e}</button>
                ))}
              </div>
            )}
          </div>
          <div style={{flex:1,background:"#fff",borderRadius:"22px",
            padding:"8px 14px",display:"flex",alignItems:"center",
            boxShadow:"0 1px 3px rgba(0,0,0,0.06)",minWidth:0}}>
            <textarea ref={textRef} value={text} onChange={e=>setText(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Message" rows={1}
              style={{flex:1,border:"none",outline:"none",background:"transparent",
                fontSize:"13px",fontFamily:"inherit",resize:"none",lineHeight:1.4,
                maxHeight:"80px",overflowY:"auto",minWidth:0}}/>
          </div>
          <input ref={fileRef} type="file" style={{display:"none"}}
            accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime"
            onChange={e=>{
              const f=e.target.files?.[0]; if(!f) return;
              if(f.type.startsWith("image/")){setImgFile(f);setImgPrev(URL.createObjectURL(f));}
              else send({type:"video",file:f});
            }}/>
          {!text.trim()
            ?<DMPlusMenu color={color} onPoll={()=>setShowPoll(s=>!s)}
                onPhoto={()=>{fileRef.current.accept="image/jpeg,image/png,image/gif";fileRef.current?.click();}}
                onVideo={()=>{fileRef.current.accept="video/mp4,video/quicktime";fileRef.current?.click();}}/>
            :<button onClick={()=>send()} disabled={sending}
              style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
                background:color,color:"#fff",fontSize:"16px",cursor:"pointer",flexShrink:0,
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
              {sending?<div style={{width:"12px",height:"12px",border:"2px solid rgba(255,255,255,0.4)",
                borderTopColor:"#fff",borderRadius:"50%",animation:"_sp 0.7s linear infinite"}}/>:"➤"}
              <style>{"@keyframes _sp{to{transform:rotate(360deg)}}"}</style>
            </button>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Conversation list ────────────────────────────────────────────────────────
function ConversationList({user,activeSub,color,onSelect,onClose,onNew}) {
  const [convs,       setConvs]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [totalUnread, setTotalUnread] = useState(0);

  const load=async()=>{
    setLoading(true);
    try{
      const r=await fetch(
        `${API}/dm_history?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
        {headers:{"Auth-Code":user.AUTH_CODE}});
      const res=await r.json();
      if(String(res.STATUS)==="1"&&Array.isArray(res.DATA)){
        setConvs(res.DATA);
        setTotalUnread(res["TOTAL UNREAD"]||0);
      }
    }catch(e){}
    setLoading(false);
  };

  useEffect(()=>{ load(); },[]);

  // Refresh list on new message
  useEffect(()=>{
    const h=()=>load();
    window.addEventListener("dm_new_message",h);
    return()=>window.removeEventListener("dm_new_message",h);
  },[]);

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"#fff"}}>
      <div style={{flexShrink:0,padding:"10px 14px",background:color,color:"#fff",
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:"15px",fontWeight:"800",display:"flex",alignItems:"center",gap:"8px"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Messages
          {totalUnread>0&&<span style={{background:"rgba(255,255,255,0.25)",borderRadius:"20px",
            padding:"1px 8px",fontSize:"11px",fontWeight:"700"}}>{totalUnread}</span>}
        </div>
        <div style={{display:"flex",gap:"6px"}}>
          <button onClick={onNew} title="New conversation"
            style={{background:"rgba(255,255,255,0.2)",border:"none",cursor:"pointer",
              color:"#fff",borderRadius:"50%",width:"26px",height:"26px",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px"}}>✏</button>
          <button onClick={onClose}
            style={{background:"rgba(255,255,255,0.2)",border:"none",cursor:"pointer",
              color:"#fff",borderRadius:"50%",width:"26px",height:"26px",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px",lineHeight:1}}>×</button>
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto"}}>
        {loading&&<div style={{textAlign:"center",padding:"30px",color:"#9CA3AF",fontSize:"12px"}}>Loading…</div>}
        {!loading&&convs.length===0&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:"36px",marginBottom:"8px",opacity:0.3}}>💬</div>
            <div style={{fontSize:"12px",color:"#9CA3AF",marginBottom:"14px"}}>No conversations yet</div>
            <button onClick={onNew}
              style={{padding:"8px 20px",border:"none",borderRadius:"20px",
                background:color,color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer"}}>
              Start a conversation
            </button>
          </div>
        )}
        {convs.map(c=>{
          const unread=c["UNREAD/DM"]||0;
          const isSelf=c.FIRSTNAME==="You";
          return(
            <div key={c.ID} onClick={()=>onSelect(c)}
              style={{display:"flex",gap:"12px",alignItems:"center",
                padding:"12px 16px",cursor:"pointer",borderBottom:"1px solid #F3F4F6",
                transition:"background 0.12s"}}
              onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              <div style={{position:"relative"}}>
                <Avatar name={c.GROUPNAME} color={color} size={44}/>
                {unread>0&&<div style={{position:"absolute",top:-2,right:-2,
                  background:color,color:"#fff",borderRadius:"50%",
                  width:"16px",height:"16px",display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:"9px",fontWeight:"800",
                  border:"2px solid #fff"}}>{unread>9?"9+":unread}</div>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:"2px"}}>
                  <span style={{fontSize:"13px",fontWeight:unread>0?"800":"700",color:"#111",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"160px"}}>
                    {c.GROUPNAME}
                  </span>
                  <span style={{fontSize:"10px",color:"#9CA3AF",flexShrink:0,marginLeft:"6px"}}>{c.TIME}</span>
                </div>
                <div style={{fontSize:"12px",color:unread>0?"#374151":"#9CA3AF",
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                  fontWeight:unread>0?"600":"400"}}>
                  {isSelf&&<span style={{color:"#9CA3AF"}}>You: </span>}
                  {c.TYPE==="image"?"📷 Photo":c.TYPE==="video"?"🎥 Video":c.MESSAGE||""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function DMPanel({user,activeSub,onClose}) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;
  const [view,       setView]       = useState("list"); // list|chat|new
  const [activeGroup,setActiveGroup]= useState(null);
  const [wsStatus,   setWsStatus]   = useState("connecting");
  const wsRef = useRef(null);

  // WebSocket — register with AUTH_CODE
  useEffect(()=>{
    if(!user?.AUTH_CODE) return;
    let ws=null, reconnectTimer=null, mounted=true;

    const connect=()=>{
      if(!mounted) return;
      ws=new WebSocket("wss://cip.medsphere.net:8082/");
      wsRef.current=ws;
      setWsStatus("connecting");

      ws.onopen=()=>{
        if(!mounted){ws.close();return;}
        setWsStatus("connected");
        ws.send(JSON.stringify({type:"register",uniqueid:user.AUTH_CODE}));
      };

      ws.onmessage=(e)=>{
        if(!mounted) return;
        try{
          const{message}=JSON.parse(e.data);
          if(!message) return;
          setWsStatus("live");

          // New message — chatresponse[0] is the full message object
          if(message.notiType==="Direct_Message"&&message.type!=="answer"&&message.type!=="Delete Chat"){
            const msg=Array.isArray(message.chatresponse)?message.chatresponse[0]:message.chatresponse;
            if(msg){
              // Ensure GROUPID is set
              if(!msg.GROUPID&&message.GROUPID) msg.GROUPID=message.GROUPID;
              window.dispatchEvent(new CustomEvent("dm_new_message",{detail:{message:msg,groupId:message.GROUPID}}));
            }
          }

          // Delete
          if(message.type==="Delete Chat"){
            window.dispatchEvent(new CustomEvent("dm_delete_message",{detail:{id:message.id||message.ID}}));
          }

          // Reaction
          if(message.event==="new_reaction"){
            window.dispatchEvent(new CustomEvent("dm_new_reaction",{detail:{dm_id:message.dm_id,reactions:message.reactions}}));
          }
        }catch(err){}
      };

      ws.onerror=()=>{ setWsStatus("error"); };
      ws.onclose=()=>{
        wsRef.current=null;
        setWsStatus("closed");
        if(mounted) reconnectTimer=setTimeout(connect,3000);
      };
    };

    const t=setTimeout(connect,50);
    return()=>{ mounted=false; clearTimeout(t); clearTimeout(reconnectTimer);
      if(ws&&ws.readyState!==WebSocket.CLOSED) ws.close(); wsRef.current=null; };
  },[user?.AUTH_CODE]);

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",position:"relative"}}>
      {/* WS status — small dot only */}
      <div style={{position:"absolute",bottom:"8px",left:"8px",zIndex:100,
        width:"8px",height:"8px",borderRadius:"50%",
        background:wsStatus==="live"||wsStatus==="connected"?"#10B981":wsStatus==="error"?"#EF4444":"#F59E0B",
        boxShadow:`0 0 0 2px #fff`,title:wsStatus}}/>

      {view==="list"&&<ConversationList user={user} activeSub={activeSub} color={color}
        onSelect={g=>{setActiveGroup(g);setView("chat");}}
        onClose={onClose} onNew={()=>setView("new")}/>}

      {view==="chat"&&activeGroup&&<DMChat group={activeGroup} user={user}
        activeSub={activeSub} color={color}
        onBack={()=>setView("list")} onClose={onClose}/>}

      {view==="new"&&<NewDM user={user} activeSub={activeSub} color={color}
        onBack={()=>setView("list")}
        onCreated={()=>setView("list")}/>}
    </div>
  );
}